import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Button, Input, Popconfirm, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import {
  normalizeEmployeeStatus,
  renderStatus,
} from "../../utils/generalUtils";

import AdminTable from "../../components/Table/AdminTable/AdminTable";

import useEmployeeService from "../../services/PositionAdmin/employeeService";
import usePositionService from "../../services/PositionAdmin/positionService";
import PivotLoading from "../../components/Loading/PivotLoading";

const { Search } = Input;

const Employees = () => {
  const { adminAddEmp } = useSelector((state) => state.generalSlice);

  const [searchValue, setSearchValue] = useState("");

  const {
    getAllEmployees,
    employeeLoading,
    employeeResponse,
    addEmployee,
    editEmployee,
    addEmployeesBulk,
    addEmployeeLoading,
    editEmployeeLoading,
    deleteEmployee,
    exportEmployees,
    exportLoading,
  } = useEmployeeService();

  const { getAllPosition, positionLoading, positionResponse } =
    usePositionService();

  const handleRefresh = (search = "") => {
    getAllEmployees(search);
    getAllPosition();
  };

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (value) => {
    setSearchValue(value);
    handleRefresh(value);
  };

  const mappedPosData = positionResponse?.data?.map((item) => ({
    value: item?.id,
    label: `${item?.position_name} - ${item?.unit}`,
  }));

  const mappedEmpData = (employeeResponse?.data || [])
    .map((item) => {
      const normalized = normalizeEmployeeStatus(item);
      return {
        key: 0,
        hasLoggedIn: item?.provider_id ? "yes" : "not yet",
        ...normalized,
      };
    })
    .filter((item) => item.is_active)
    .map((item, index) => ({ ...item, key: index + 1 }));

  const adminEmpData = {
    employee: mappedEmpData ? [...mappedEmpData] : [],
    position: mappedPosData ? [...mappedPosData] : [],
  };

  const handleDelete = async (id) => {
    try {
      await deleteEmployee(id);
      message.success("Employee deleted successfully.");
      handleRefresh(searchValue);
    } catch {
      message.error("Failed to delete employee.");
    }
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "First Name",
      dataIndex: "first_name",
      key: "first_name",
      sortable: true,
    },
    {
      title: "Middle Name",
      dataIndex: "middle_name",
      key: "middle_name",
      sortable: true,
    },
    {
      title: "Last Name",
      dataIndex: "last_name",
      key: "last_name",
      sortable: true,
    },
    {
      title: "First Login",
      dataIndex: "hasLoggedIn",
      key: "hasLoggedIn",
      render: renderStatus(["none"], ["yes"], ["no", "not yet"]),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sortable: true,
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        const isNonUpou = !record.email?.endsWith("@upou.edu.ph");
        if (!isNonUpou) return null;
        return (
          <Popconfirm
            title="Delete this employee?"
            description={`This will permanently remove ${record.email}.`}
            onConfirm={(e) => { e.stopPropagation(); handleDelete(record.id); }}
            onCancel={(e) => e.stopPropagation()}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        );
      },
    },
  ];

  const formFields = [
    {
      label: "Employee Id",
      name: "id",
      // disabled: true,
      hidden: true,
    },
    {
      label: "First Name",
      name: "first_name",
      rules: [{ required: true, message: "First Name is required" }],
    },
    {
      label: "Middle Name",
      name: "middle_name",
      // rules: [{ required: true, message: "Middle Name is required" }],
    },
    {
      label: "Last Name",
      name: "last_name",
      rules: [{ required: true, message: "Last Name is required" }],
    },
    {
      label: "Department",
      name: "department",
    },
    {
      label: "Employment End Date",
      name: "employment_end_date",
      hidden: true,
    },
    {
      label: "Active",
      name: "is_active",
      type: "switch",
    },
    {
      label: "Positions",
      name: "positions",
      type: "group",
      groupName: "positions",
      objectName: "position_name",
      objectKey: "position_id",
    },
    {
      label: "Email",
      name: "email",
      disabledEdit: true,
      // rules: [{ required: true }],
    },
  ];

  const pageLoading = employeeLoading || positionLoading;
  const submitLoading = addEmployeeLoading || editEmployeeLoading;

  return (
    <>
      {pageLoading && <PivotLoading />}
      {!pageLoading && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h1>Manage Employees</h1>
            <Button
              onClick={exportEmployees}
              loading={exportLoading}
              style={{ marginBottom: 12 }}
            >
              Export to Excel
            </Button>
            <Search
              placeholder="Search name and email..."
              value={searchValue}
              size="large"
              allowClear
              enterButton
              onSearch={handleSearch}
              onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);
                if (val === "") {
                  handleRefresh("");
                }
              }}
              style={{ marginBottom: 12, maxWidth: 500 }}
            />
          </div>

          <AdminTable
            tableColumn={columns}
            tableData={mappedEmpData}
            tableType="adminEmployee"
            responseData={adminEmpData}
            popupSlice={adminAddEmp}
            popupName="adminAddEmp"
            docuType="Employee"
            formFields={formFields}
            onRefresh={() => handleRefresh(searchValue)}
            onAdd={addEmployee}
            onEdit={editEmployee}
            onAddBulk={addEmployeesBulk}
            submitLoading={submitLoading}
            isEmployee={true}
            positionData={mappedPosData}
          />
        </>
      )}
    </>
  );
};

export default Employees;
